import React, { useState } from 'react';
import { Button } from '../../ui/Button';
import { Slideover, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui';
import { settingsAPI } from '../../../services/api';

// IDI-43: IDI loads a dealer's CSV (IDI-671: or .xlsx) price sheet in its workspace Item Catalog.
// Preview is action=price_upload (IDI-669/698); Save resends the same file
// with "save": true (IDI-670). Copy is the ticket's, verbatim.
const INTRO = 'A CSV or Excel (.xlsx) file with a Part # (or SKU) column and a Cost column. Active (Yes or No) is optional. Nothing is saved until you choose Save.';
// As the list's Cost/Unit shows it: $0 / blank is '—'.
const cost = (c) => (c ? `$${(Number(c) / 100).toFixed(2)}` : '—');
const active = (a) => (a ? 'Active' : 'Inactive');
const pair = (a, b) => (a === b ? a : `${a} → ${b}`);
const prices = (n) => `Saved ${n} ${n === 1 ? 'price' : 'prices'}.`;

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function PriceUploadSheet({ open, onClose, nameBySku, onSaved, toast }) {
  const [file, setFile] = useState(null);
  const [encoded, setEncoded] = useState('');
  const [answer, setAnswer] = useState(null);
  const [message, setMessage] = useState('');
  const [footerLine, setFooterLine] = useState('');
  const [running, setRunning] = useState('');

  const reset = () => { setAnswer(null); setMessage(''); setFooterLine(''); };
  const close = () => { if (running) return; setFile(null); setEncoded(''); reset(); onClose(); };

  const pick = (e) => {
    reset();
    setEncoded('');
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const preview = async () => {
    if (!file) return;
    reset();
    if (!/\.(csv|xlsx)$/i.test(file.name.trim())) { setMessage('Choose a CSV or Excel (.xlsx) file.'); return; }
    setRunning('preview');
    try {
      const b64 = await readBase64(file);
      setEncoded(b64);
      setAnswer(await settingsAPI.uploadPrices({ file_name: file.name, file_base64: b64 }));
    } catch (err) {
      setMessage(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setRunning('');
    }
  };

  const save = async () => {
    if (!answer || !answer.changed || running) return;
    setRunning('save');
    setFooterLine('');
    try {
      const res = await settingsAPI.uploadPrices({ file_name: file.name, file_base64: encoded, save: true });
      const failed = Array.isArray(res?.failed) ? res.failed : [];
      if (failed.length === 0) {
        toast.success(prices(res?.saved || 0));
        setRunning('');
        setFile(null); setEncoded(''); reset(); onClose();
      } else {
        setAnswer(null);
        setFooterLine(`${prices(res?.saved || 0)} ${failed.length} failed: ${failed.map((f) => f.part_number).join(', ')}. Upload the file again to retry them.`);
      }
    } catch (err) {
      setFooterLine(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setRunning('');
      onSaved();
    }
  };

  const changes = answer?.changes || [];
  const problems = answer?.problems || [];
  const n = answer?.changed || 0;
  const footer = (
    <div className="flex w-full items-center gap-3">
      <p className="flex-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>{footerLine || INTRO}</p>
      <Button variant="secondary" onClick={close} disabled={running === 'save'}>Cancel</Button>
      <Button onClick={save} loading={running === 'save'} disabled={!n || running !== ''}>
        {`Save ${n} ${n === 1 ? 'change' : 'changes'}`}
      </Button>
    </div>
  );

  return (
    <Slideover open={open} onClose={close} title="Upload prices" width="xl" footer={footer}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" aria-label="Price file" onChange={pick} disabled={running !== ''} />
          <Button variant="secondary" onClick={preview} loading={running === 'preview'} disabled={!file || running !== ''}>Preview</Button>
        </div>
        {message && <p className="text-sm" style={{ color: 'var(--color-text-danger)' }}>{message}</p>}
        {answer && (
          <>
            <p className="text-sm font-medium">
              {`Matched ${answer.matched} (${answer.changed} will change, ${answer.unchanged} unchanged) · Unknown ${answer.unknown} · Not saved ${answer.not_saved}`}
            </p>
            <h3 className="text-sm font-semibold">Will change</h3>
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHead><tr><TableHeader>Part #</TableHeader><TableHeader>Item</TableHeader><TableHeader align="right">Cost/Unit</TableHeader><TableHeader>Status</TableHeader></tr></TableHead>
                <TableBody>
                  {changes.map((c) => (
                    <TableRow key={`${c.row}-${c.sku}`}>
                      <TableCell>{c.part_number || '—'}</TableCell>
                      <TableCell>{nameBySku.get(String(c.sku)) || c.name}</TableCell>
                      <TableCell align="right">{pair(cost(c.cost_cents_before), cost(c.cost_cents_after))}</TableCell>
                      <TableCell>{pair(active(c.active_before), active(c.active_after))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <h3 className="text-sm font-semibold">Unknown and not saved</h3>
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHead><tr><TableHeader align="right">Row</TableHeader><TableHeader>Part #</TableHeader><TableHeader>Reason</TableHeader></tr></TableHead>
                <TableBody>
                  {problems.map((p) => (
                    <TableRow key={`${p.row}-${p.part_number}`}>
                      <TableCell align="right">{p.row}</TableCell>
                      <TableCell>{p.part_number || '—'}</TableCell>
                      <TableCell>{p.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Slideover>
  );
}
